"use client";

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { OrganizationMembersList } from '@/components/settings/OrganizationMembersList';
import { useIsOrganizationOwner } from '@/hooks/useIsOrganizationOwner';
import { useOrganizationBusinessRules, OrganizationBusinessRules } from '@/hooks/useOrganizationBusinessRules';
import { MultiCertificationSelector } from '@/components/settings/MultiCertificationSelector';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useUserRole } from '@/hooks/useUserRole';
import { featureFlags, FEATURE_FLAGS } from '@/lib/featureFlags';

export default function OrganizationSettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isOwner = useIsOrganizationOwner();
  const { isClient } = useUserRole();
  const organizationId = user?.organization_id;
  const { rules, isLoading: rulesLoading, updateRules, isUpdating } = useOrganizationBusinessRules(organizationId);
  const [enhancedEnabled, setEnhancedEnabled] = useState(true);
  const [premiumEnabled, setPremiumEnabled] = useState(true);

  // Check feature flags for verification levels
  useEffect(() => {
    const checkFlags = async () => {
      const [enhanced, premium] = await Promise.all([
        featureFlags.isEnabled(FEATURE_FLAGS.ENHANCED_VERIFICATION),
        featureFlags.isEnabled(FEATURE_FLAGS.PREMIUM_VERIFICATION)
      ]);
      setEnhancedEnabled(enhanced);
      setPremiumEnabled(premium);
    };
    checkFlags();
  }, []);

  // Redirect non-owners
  useEffect(() => {
    if (user && !isOwner) {
      navigate('/dashboard');
    }
  }, [isOwner, user, navigate]);

  if (!organizationId) {
    return (
      <div className="container max-w-4xl py-8">
        <div className="text-center text-muted-foreground">
          You are not part of an organization.
        </div>
      </div>
    );
  }

  if (rulesLoading) {
    return (
      <div className="container max-w-4xl py-8">
        <div className="text-center text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // Get current rules or defaults
  const currentRules: OrganizationBusinessRules = (rules as OrganizationBusinessRules) || {
    organization_id: organizationId,
    minimum_vendors_invited: 0,
    minimum_vendor_verification_level: 'none' as const,
    minimum_vendor_rating: 0,
    minimum_vendor_completed_jobs: 0,
    required_certification_ids: [],
    auto_accept_completion_days: 7,
  };

  const updateRule = (key: string, value: any) => {
    const updated: Partial<OrganizationBusinessRules> = { 
      ...currentRules, 
      organization_id: organizationId,
      [key]: value 
    };
    updateRules(updated);
  };

  return (
    <div className="container max-w-4xl py-8 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Organization Settings</h1>
        <p className="text-muted-foreground">
          Manage team members, business rules, and notification preferences
        </p>
      </div>

      <div className="grid gap-6">
        {/* Team Members Section */}
        <Card>
          <CardHeader>
            <CardTitle>Team Members</CardTitle>
            <CardDescription>Manage your organization's team and invitations</CardDescription>
          </CardHeader>
          <CardContent>
            <OrganizationMembersList organizationId={organizationId} />
          </CardContent>
        </Card>

        {/* Business Rules Section - Only visible to client organizations */}
        {isClient && (
          <Card>
            <CardHeader>
              <CardTitle>Vendor Eligibility Rules</CardTitle>
              <CardDescription>
                <span className="text-amber-600 dark:text-amber-400 font-medium">⚠️ Hard exclusion filters:</span> Vendors who do not meet these criteria will be completely hidden from all searches for your organization. These rules apply to all team members.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Vendor Eligibility Rules */}
              <div className="space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor="verificationLevel">Minimum Vendor Verification Level</Label>
                  <Select
                    value={currentRules.minimum_vendor_verification_level}
                    onValueChange={(value) => updateRule('minimum_vendor_verification_level', value)}
                  >
                    <SelectTrigger id="verificationLevel">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None - Show all vendors</SelectItem>
                      <SelectItem value="basic">Basic - Email verified only</SelectItem>
                      {enhancedEnabled && (
                        <SelectItem value="enhanced">Enhanced - Business verified</SelectItem>
                      )}
                      {premiumEnabled && (
                        <SelectItem value="premium">Premium - Full verification</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    Vendors below this verification level will be hidden from search results
                  </p>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="minRating">Minimum Vendor Rating</Label>
                  <Input
                    id="minRating"
                    type="number"
                    min="0"
                    max="5"
                    step="0.5"
                    value={currentRules.minimum_vendor_rating}
                    onChange={(e) => updateRule('minimum_vendor_rating', parseFloat(e.target.value) || 0)}
                  />
                  <p className="text-sm text-muted-foreground">
                    Vendors with ratings below this will be hidden (0 = no minimum)
                  </p>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="minJobs">Minimum Completed Jobs</Label>
                  <Input
                    id="minJobs"
                    type="number"
                    min="0"
                    max="100"
                    value={currentRules.minimum_vendor_completed_jobs}
                    onChange={(e) => updateRule('minimum_vendor_completed_jobs', parseInt(e.target.value) || 0)}
                  />
                  <p className="text-sm text-muted-foreground">
                    Vendors with fewer completed jobs will be hidden (0 = no minimum)
                  </p>
                </div>

                <div className="grid gap-2">
                  <Label>Required Vendor Certifications</Label>
                  <MultiCertificationSelector
                    selectedCertificationIds={currentRules.required_certification_ids}
                    onChange={(certIds) => updateRule('required_certification_ids', certIds)}
                  />
                  <p className="text-sm text-muted-foreground">
                    Vendors must have ALL selected certifications to appear in search results
                  </p>
                </div>
              </div>

              <Separator />

              {/* RFQ Requirements */}
              <div>
                <h3 className="text-lg font-medium mb-4">RFQ Requirements</h3>
                <div className="grid gap-2">
                  <Label htmlFor="minVendors">Minimum Vendors to Invite</Label>
                  <Input
                    id="minVendors"
                    type="number"
                    min="0"
                    max="10"
                    value={currentRules.minimum_vendors_invited}
                    onChange={(e) => updateRule('minimum_vendors_invited', parseInt(e.target.value) || 0)}
                  />
                  <p className="text-sm text-muted-foreground">
                    RFQs cannot be published unless this many vendors are invited (private) or match criteria (public). Set to 0 for no minimum.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

      </div>
    </div>
  );
}
